import { useCallback, useEffect, useMemo, useState } from "react";
import { getAllEmailTemplates } from "../../../api/v2/TasklistsApi";
import { EmailTemplateSummaryDto } from "../../../dtos/EmailTemplateSummaryDto";

export type ViewMode = "grid" | "tree";

export interface UsageTreeNode {
    id: string;
    parentId: string | null;
    label: string;
    nodeType: "module" | "task" | "subtask" | "template";
    templateName: string | null;
    eventType: string | null;
    trigger: string | null;
}

// Builds a flat array of tree nodes from the template data.
// Hierarchy: Module > Task > Subtask? > Template (leaf)
function buildUsageTree(templates: EmailTemplateSummaryDto[]): UsageTreeNode[] {
    const nodes: UsageTreeNode[] = [];
    const moduleIds = new Map<string, string>();
    const taskIds = new Map<string, string>();
    const subtaskIds = new Map<string, string>();

    for (const t of templates) {
        for (const u of t.usages) {
            // Module node
            const moduleKey = u.moduleName;
            if (!moduleIds.has(moduleKey)) {
                const id = `mod_${moduleKey}`;
                moduleIds.set(moduleKey, id);
                nodes.push({ id, parentId: null, label: `Module: ${u.moduleName}`, nodeType: "module", templateName: null, eventType: null, trigger: null });
            }
            const moduleId = moduleIds.get(moduleKey)!;

            // Task node
            const taskKey = `${u.moduleName}|${u.taskName}`;
            if (!taskIds.has(taskKey)) {
                const id = `task_${taskKey}`;
                taskIds.set(taskKey, id);
                nodes.push({ id, parentId: moduleId, label: `Task: ${u.taskName}`, nodeType: "task", templateName: null, eventType: null, trigger: null });
            }
            const taskId = taskIds.get(taskKey)!;

            let leafParentId = taskId;

            // Subtask node (if applicable)
            if (u.subtaskName) {
                const subtaskKey = `${taskKey}|${u.subtaskName}`;
                if (!subtaskIds.has(subtaskKey)) {
                    const id = `sub_${subtaskKey}`;
                    subtaskIds.set(subtaskKey, id);
                    nodes.push({ id, parentId: taskId, label: `Subtask: ${u.subtaskName}`, nodeType: "subtask", templateName: null, eventType: null, trigger: null });
                }
                leafParentId = subtaskIds.get(subtaskKey)!;
            }

            // Template leaf node (unique per parent + template + trigger)
            const leafId = `tmpl_${leafParentId}_${t.name}_${u.trigger}`;
            if (!nodes.some(n => n.id === leafId)) {
                nodes.push({ id: leafId, parentId: leafParentId, label: t.name, nodeType: "template", templateName: t.name, eventType: t.eventType, trigger: u.trigger });
            }
        }
    }

    return nodes;
}

export default function useEmailTemplatesController() {
    const [templates, setTemplates] = useState<EmailTemplateSummaryDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedTemplateName, setSelectedTemplateName] = useState<string | null>(null);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [viewMode, setViewMode] = useState<ViewMode>("grid");

    useEffect(() => {
        (async () => {
            setLoading(true);
            const result = await getAllEmailTemplates();
            if (result?.data) setTemplates(result.data);
            setLoading(false);
        })();
    }, []);

    const treeNodes = useMemo(() => buildUsageTree(templates), [templates]);

    const handleRowClick = useCallback((templateName: string) => {
        setSelectedTemplateName(templateName);
        setDialogVisible(true);
    }, []);

    const handleDialogHiding = useCallback(() => {
        setDialogVisible(false);
        setSelectedTemplateName(null);
    }, []);

    return {
        templates,
        treeNodes,
        loading,
        selectedTemplateName,
        dialogVisible,
        viewMode,
        setViewMode,
        handleRowClick,
        handleDialogHiding,
    };
}
